import React, { useState, useEffect, useRef } from 'react';
import * as Icons from 'lucide-react';

interface Props {
  value: string;
  onChange: (value: string) => void;
}

interface NominatimResult {
  place_id: number;
  display_name: string;
  lat: string;
  lon: string;
}

export const AddressAutocomplete: React.FC<Props> = ({ value, onChange }) => {
  const [suggestions, setSuggestions] = useState<NominatimResult[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  
  useEffect(() => {
    const timer = setTimeout(() => {
      if (value.length > 2 && isOpen) {
        fetchSuggestions(value);
      }
    }, 800);

    return () => clearTimeout(timer);
  }, [value, isOpen]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchSuggestions = async (query: string) => {
    setIsLoading(true);
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&addressdetails=1&limit=5&accept-language=ru&countrycodes=ru`
      );
      if (response.ok) {
        const data = await response.json();
        setSuggestions(data);
      }
    } catch (error) {
      console.error('Error fetching address:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleGeolocation = () => {
    if (!navigator.geolocation) {
      alert('Геолокация не поддерживается вашим браузером');
      return;
    }

    setIsLoading(true);
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          const { latitude, longitude } = position.coords;
          const response = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&accept-language=ru`
          );
          if (response.ok) {
            const data = await response.json();
            const address = data.display_name.split(',').slice(0, 3).join(',');
            onChange(address);
            setIsOpen(false);
          }
        } catch (error) {
          console.error('Error with geolocation:', error);
          alert('Не удалось определить местоположение');
        } finally {
          setIsLoading(false);
        }
      },
      (error) => {
        console.error('Geolocation permission denied:', error);
        setIsLoading(false);
        alert('Пожалуйста, разрешите доступ к геолокации');
      }
    );
  };

  const handleSelect = (address: string) => {
    const cleanAddress = address.split(',').slice(0, 4).join(',');
    onChange(cleanAddress);
    setIsOpen(false);
  };

  return (
    <div className="relative" ref={wrapperRef}>
      <div className="relative group">
        <Icons.MapPin className="absolute left-4 top-4 text-gray-400 transition-colors group-focus-within:text-primary" size={20} />
        
        <input 
          type="text" 
          placeholder="Улица, дом, квартира"
          value={value}
          onChange={(e) => {
            onChange(e.target.value);
            setIsOpen(true);
          }}
          onFocus={() => value.length > 2 && setIsOpen(true)}
          className="w-full pl-12 pr-12 py-4 rounded-2xl bg-gray-50 border-none outline-none text-gray-800 font-medium placeholder-gray-400 focus:ring-2 focus:ring-primary/20 focus:bg-white transition-all shadow-inner"
        />

        <div className="absolute right-3 top-3">
          {isLoading ? (
            <div className="p-1">
                 <Icons.Loader2 className="animate-spin text-primary" size={20} />
            </div>
          ) : value ? (
            <button onClick={() => onChange('')} className="p-1 text-gray-400 hover:text-gray-600 transition-colors rounded-full hover:bg-gray-200">
               <Icons.X size={20} />
            </button>
          ) : (
            <button onClick={handleGeolocation} className="p-1 text-primary hover:text-blue-600 transition-colors rounded-full hover:bg-blue-50">
               <Icons.Crosshair size={20} />
            </button>
          )}
        </div>
      </div>

      {isOpen && suggestions.length > 0 && (
        <ul className="absolute z-50 w-full bg-white mt-3 rounded-2xl shadow-3d border border-gray-100 overflow-hidden animate-fadeIn">
          {suggestions.map((item) => (
            <li 
              key={item.place_id}
              onClick={() => handleSelect(item.display_name)}
              className="px-5 py-4 hover:bg-gray-50 cursor-pointer border-b border-gray-50 last:border-0 text-sm flex items-start gap-3 transition-colors"
            >
              <Icons.MapPin size={18} className="mt-0.5 text-primary/60 shrink-0" />
              <span className="text-gray-700 leading-snug">{item.display_name}</span>
            </li>
          ))}
          <li className="bg-gray-50/50 px-4 py-2.5 text-[10px] uppercase font-bold text-gray-400 text-center tracking-wider">
            Данные OpenStreetMap
          </li>
        </ul>
      )}
    </div>
  );
};